import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';

// Security Context
const SecurityContext = createContext(null);

export const useSecurity = () => {
  const context = useContext(SecurityContext);
  if (!context) throw new Error('useSecurity must be used within SecurityProvider');
  return context;
};

// Constants
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const SESSION_WARNING = 5 * 60 * 1000; // 5 minutes before timeout
const AUDIT_LOG_KEY = 'destiny_audit_log';
const SESSION_KEY = 'destiny_session';
const RATE_LIMIT_KEY = 'destiny_rate_limit';
const TWO_FA_KEY = 'destiny_2fa';
const TWO_FA_VERIFIED_KEY = 'destiny_2fa_verified';

// Generate 6-digit OTP
export const generateOTP = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Audit Log Functions
export const addAuditLog = (userId, userName, action, details = {}) => {
  const logs = getAuditLogs();
  const newLog = {
    id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    userId,
    userName,
    action,
    details,
    timestamp: new Date().toISOString(),
    ipAddress: 'localhost', // In production, get from server
    userAgent: navigator.userAgent
  };
  logs.unshift(newLog);
  // Keep only last 1000 logs
  const trimmedLogs = logs.slice(0, 1000);
  localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(trimmedLogs));
  return newLog;
};

export const getAuditLogs = () => {
  const saved = localStorage.getItem(AUDIT_LOG_KEY);
  return saved ? JSON.parse(saved) : [];
};

export const clearAuditLogs = () => {
  localStorage.removeItem(AUDIT_LOG_KEY);
};

// Rate Limiting Functions
const getRateLimitData = () => {
  const saved = localStorage.getItem(RATE_LIMIT_KEY);
  return saved ? JSON.parse(saved) : {};
};

const setRateLimitData = (data) => {
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
};

export const checkRateLimit = (action, maxAttempts = 5, windowMs = 60000) => {
  const data = getRateLimitData();
  const now = Date.now();
  const key = action;

  if (!data[key]) {
    data[key] = { attempts: [], blocked_until: null };
  }

  // Check if blocked
  if (data[key].blocked_until && now < data[key].blocked_until) {
    const remainingMs = data[key].blocked_until - now;
    return {
      allowed: false,
      remainingMs,
      message: `Terlalu banyak percubaan. Cuba lagi dalam ${Math.ceil(remainingMs / 1000)} saat.`
    };
  }

  // Clear old attempts outside window
  data[key].attempts = data[key].attempts.filter(t => now - t < windowMs);
  data[key].blocked_until = null;

  // Check if over limit
  if (data[key].attempts.length >= maxAttempts) {
    data[key].blocked_until = now + windowMs;
    setRateLimitData(data);
    return {
      allowed: false,
      remainingMs: windowMs,
      message: `Terlalu banyak percubaan. Cuba lagi dalam ${Math.ceil(windowMs / 1000)} saat.`
    };
  }

  // Record attempt
  data[key].attempts.push(now);
  setRateLimitData(data);

  return {
    allowed: true,
    attemptsRemaining: maxAttempts - data[key].attempts.length
  };
};

export const clearRateLimit = (action) => {
  const data = getRateLimitData();
  delete data[action];
  setRateLimitData(data);
};

// 2FA Functions
export const is2FAEnabled = (userId) => {
  const data = localStorage.getItem(`${TWO_FA_KEY}_${userId}`);
  return data === 'true';
};

export const enable2FA = (userId) => {
  localStorage.setItem(`${TWO_FA_KEY}_${userId}`, 'true');
};

export const disable2FA = (userId) => {
  localStorage.removeItem(`${TWO_FA_KEY}_${userId}`);
  localStorage.removeItem(`${TWO_FA_VERIFIED_KEY}_${userId}`);
};

export const is2FAVerified = (userId) => {
  const sessionData = localStorage.getItem(SESSION_KEY);
  if (!sessionData) return false;
  const session = JSON.parse(sessionData);
  return session.twoFAVerified === true;
};

export const set2FAVerified = (userId, verified) => {
  const sessionData = localStorage.getItem(SESSION_KEY);
  if (sessionData) {
    const session = JSON.parse(sessionData);
    session.twoFAVerified = verified;
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }
};

// Session Functions
export const createSession = (userId) => {
  const session = {
    userId,
    createdAt: Date.now(),
    lastActivity: Date.now(),
    twoFAVerified: false
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
};

export const getSession = () => {
  const saved = localStorage.getItem(SESSION_KEY);
  return saved ? JSON.parse(saved) : null;
};

export const updateSessionActivity = () => {
  const session = getSession();
  if (session) {
    session.lastActivity = Date.now();
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }
};

export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
};

export const isSessionExpired = () => {
  const session = getSession();
  if (!session) return true;
  return Date.now() - session.lastActivity > SESSION_TIMEOUT;
};

export const getSessionRemainingTime = () => {
  const session = getSession();
  if (!session) return 0;
  const elapsed = Date.now() - session.lastActivity;
  return Math.max(0, SESSION_TIMEOUT - elapsed);
};

// Security Provider Component
export const SecurityProvider = ({ children, user, onLogout }) => {
  const [sessionWarning, setSessionWarning] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(SESSION_TIMEOUT);

  // Activity listener
  const resetActivityTimer = useCallback(() => {
    if (user) {
      updateSessionActivity();
      setSessionWarning(false);
      setShowTimeoutModal(false);
    }
  }, [user]);

  // Check session timeout
  useEffect(() => {
    if (!user) return;

    // Ensure session exists and is fresh when user is present
    const session = getSession();
    if (!session || session.userId !== user.user_id) {
      createSession(user.user_id || user.id);
    } else {
      updateSessionActivity();
    }

    const checkSession = () => {
      const remaining = getSessionRemainingTime();
      setRemainingTime(remaining);

      if (remaining <= 0) {
        // Session expired
        addAuditLog(user.id, user.name, 'SESSION_TIMEOUT', { reason: 'Inactivity timeout' });
        clearSession();
        onLogout?.();
        setShowTimeoutModal(true);
      } else if (remaining <= SESSION_WARNING) {
        setSessionWarning(true);
      }
    };

    const interval = setInterval(checkSession, 10000); // Check every 10 seconds
    // Don't check immediately - give the session time to initialize
    const initialCheck = setTimeout(checkSession, 2000);

    return () => { clearInterval(interval); clearTimeout(initialCheck); };
  }, [user, onLogout]);

  // Activity listeners
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, resetActivityTimer));

    return () => {
      events.forEach(event => document.removeEventListener(event, resetActivityTimer));
    };
  }, [user, resetActivityTimer]);

  const extendSession = useCallback(() => {
    resetActivityTimer();
    addAuditLog(user?.id, user?.name, 'SESSION_EXTENDED', {});
  }, [user, resetActivityTimer]);

  const logAction = useCallback((action, details = {}) => {
    if (user) {
      return addAuditLog(user.id, user.name, action, details);
    }
  }, [user]);

  return (
    <SecurityContext.Provider value={{
      sessionWarning,
      showTimeoutModal,
      remainingTime,
      extendSession,
      logAction,
      checkRateLimit,
      clearRateLimit,
      setShowTimeoutModal
    }}>
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityContext;
